import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../services/api';
import { format } from 'date-fns';
import { Eye, Plus } from 'lucide-react';
import { Modal } from '@/components/ui';
import { PaymentModal } from './payment_modal';

export const PaymentDetails = ({ customerId, ordersList, initialOrderId, lead }) => {
  const safeOrdersList = Array.isArray(ordersList) ? ordersList.filter(o => o.status !== 'SAVED') : [];
  const [selectedOrderId, setSelectedOrderId] = useState(initialOrderId || (safeOrdersList.length > 0 ? safeOrdersList[0]._id : null));
  const order = safeOrdersList.find(o => o._id === selectedOrderId) || safeOrdersList[0] || null;

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['recieved-amounts', customerId],
    queryFn: async () => {
      if (!customerId) return null;
      const res = await api.get(`/accounts/recieved_amounts?customer_id=${customerId}`);
      const records = res.data?.data || [];
      const allPayments = records.flatMap(r => r.payments || []);
      return { payments: allPayments };
    },
    enabled: !!customerId,
  });

  const payments = data?.payments || [];
  
  const calculateGrandTotal = (currentOrder) => {
    if (!currentOrder || !currentOrder.items || currentOrder.items.length === 0) return 0;
    
    // Calculate subtotal
    const subtotal = currentOrder.items.reduce((sum, item) => {
      const qty = Number(item.qty) || Number(item.quantity) || 1;
      const rate = Number(item.item_details?.price || item.item_details?.purchaseRate || item.item_details?.rate || item.price || item.unit_price || 0);
      return sum + (qty * rate);
    }, 0);

    // Calculate discount
    const discountType = currentOrder.discount_type || '%';
    const discountValue = currentOrder.discount_value || 0;
    let discountAmount = 0;
    if (discountType === '%' || discountType === 'percentage') {
      discountAmount = Math.round(subtotal * (discountValue / 100));
    } else {
      discountAmount = Number(discountValue);
    }

    const taxableValue = subtotal - discountAmount;
    
    // Calculate GST
    const gstValue = Number(currentOrder.gst_value) || 0;
    const gstAmount = Math.round(taxableValue * (gstValue / 100));
    
    return taxableValue + gstAmount;
  };

  // Calculate summary
  const totalReceived = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const totalAmount = order?.amount || order?.grandTotal || calculateGrandTotal(order) || lead?.total_amount || 0;
  const outstanding = Math.max(0, totalAmount - totalReceived);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 w-full flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:max-w-lg">
          <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">Select Order:</span>
          <select 
            className="w-full border border-slate-300 rounded-md text-sm px-3 py-1.5 focus:outline-none focus:border-black bg-slate-50 text-slate-700 font-medium cursor-pointer"
            value={selectedOrderId || ''}
            onChange={(e) => setSelectedOrderId(e.target.value)}
          >
            {safeOrdersList.length === 0 && <option value="" disabled>No Orders Found</option>}
            {safeOrdersList.map((est, idx) => {
              const estNum = safeOrdersList.length - idx;
              const dateStr = new Date(est.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <option key={est._id} value={est._id}>
                  Order #{estNum} — {dateStr} ({est.status.replace(/_/g, ' ')})
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 w-full relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">{lead?.firstName} {lead?.lastName} Ledger</h2>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 bg-[#fef3c7] text-[#92400e] text-xs font-bold rounded-full tracking-wide">
            Payment Details
          </span>
          <button 
            onClick={() => setShowPaymentModal(true)}
            className="px-4 py-1.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold hover:bg-black shadow-sm transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
          >
            <Plus size={16} /> Record
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
          <p className="text-sm text-slate-500 font-medium mb-1">Total Amount</p>
          <p className="text-2xl font-bold text-slate-900">₹{Number(totalAmount).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
          <p className="text-sm text-emerald-700 font-medium mb-1">Total Received</p>
          <p className="text-2xl font-bold text-emerald-900">₹{totalReceived.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-red-50 rounded-xl p-5 border border-red-100">
          <p className="text-sm text-red-700 font-medium mb-1">Outstanding</p>
          <p className="text-2xl font-bold text-red-900">₹{outstanding.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Invoice Table */}
      <div>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500">
              <th className="pb-3 font-medium">Date</th>
              <th className="pb-3 font-medium">Amount Paid</th>
              <th className="pb-3 font-medium">Mode of Payment</th>
              <th className="pb-3 font-medium">Notes</th>
              <th className="pb-3 font-medium text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {payments.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-8 text-center text-slate-500">No payment records found.</td>
              </tr>
            ) : (
              payments.map((payment, idx) => (
                <tr key={payment._id || idx}>
                  <td className="py-4 text-slate-800 font-medium">
                    {payment.date ? format(new Date(payment.date), 'dd MMM yyyy') : '-'}
                  </td>
                  <td className="py-4 text-slate-800 font-medium text-emerald-600">₹{Number(payment.amount).toLocaleString('en-IN')}</td>
                  <td className="py-4 text-slate-600 capitalize">
                    {payment.paymentMethod || '-'} 
                    {payment.bankName ? ` (${payment.bankName})` : ''}
                  </td>
                  <td className="py-4 text-slate-600 max-w-[200px] truncate" title={payment.remarks}>
                    {payment.remarks || '-'}
                  </td>
                  <td className="py-4 text-center">
                    <button 
                      className="p-1.5 text-slate-400 hover:text-[#C91D25] hover:bg-red-50 rounded-lg transition-colors inline-flex"
                      title="View Details"
                      onClick={() => setSelectedPayment(payment)}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!selectedPayment} onClose={() => setSelectedPayment(null)} title="Payment Record Details" size="max-w-lg">
        {selectedPayment && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Amount Paid</span>
                <span className="text-sm font-semibold text-emerald-700">₹{Number(selectedPayment.amount).toLocaleString('en-IN')}</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Date</span>
                <span className="text-sm font-medium text-slate-800">
                  {selectedPayment.date ? format(new Date(selectedPayment.date), 'dd MMM yyyy') : '—'}
                </span>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Mode of Payment</span>
                <span className="text-sm font-medium text-slate-800 capitalize">{selectedPayment.paymentMethod || '—'}</span>
              </div>
              
              {selectedPayment.paymentMethod === 'bank' && (
                <>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Bank Name</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.bankName || '—'}</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Transaction ID</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.transactionId || '—'}</span>
                  </div>
                </>
              )}
              
              {selectedPayment.paymentMethod === 'upi' && (
                <>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">UPI Phone</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.upiPhone || '—'}</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">UPI Ref No</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.upiRefNo || '—'}</span>
                  </div>
                </>
              )}
              
              {selectedPayment.paymentMethod === 'cash' && (
                <>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Receiver Name</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.receiverName || '—'}</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Receiver Phone</span>
                    <span className="text-sm font-medium text-slate-800">{selectedPayment.receiverPhone || '—'}</span>
                  </div>
                </>
              )}
              
              {selectedPayment.remarks && (
                <div className="col-span-2 bg-slate-50 p-3 rounded-lg">
                  <span className="block text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">Notes / Remarks</span>
                  <span className="text-sm font-medium text-slate-800">{selectedPayment.remarks}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <PaymentModal 
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currentOrder={order || { customer_id: customerId, estimation_id: estimationId }}
        grandTotal={totalAmount}
        onPaymentComplete={refetch}
      />
    </div>
    </div>
  );
};
